import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { IoHourglass, IoTimeOutline } from 'react-icons/io5';
import { MdAccessTime, MdEventBusy } from 'react-icons/md';
import { FaChevronRight } from 'react-icons/fa';
import ExpiryModal from './stat-card-modal/ExpiryModal';

const StatCard = () => {
  const { authState } = useContext(AuthContext);
  const [expiryAnalysis, setExpiryAnalysis] = useState({
    expiringIn90Days: [],
    expiringIn30Days: [],
    expiringIn7Days: [],
    expiredToday: [],
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchExpiryAnalysis = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts`,
          {
            headers: {
              'x-auth-token': authState.token,
            },
          }
        );
        const contracts = response.data;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expiryContracts = {
          expiringIn90Days: [],
          expiringIn30Days: [],
          expiringIn7Days: [],
          expiredToday: [],
        };

        contracts.forEach((contract) => {
          const endDate = new Date(contract.endDate);
          endDate.setHours(0, 0, 0, 0);

          const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

          if (daysRemaining === 0) {
            expiryContracts.expiredToday.push({ ...contract, daysRemaining });
          } else if (daysRemaining > 0 && daysRemaining <= 7) {
            expiryContracts.expiringIn7Days.push({ ...contract, daysRemaining });
          } else if (daysRemaining > 7 && daysRemaining <= 30) {
            expiryContracts.expiringIn30Days.push({ ...contract, daysRemaining });
          } else if (daysRemaining > 30 && daysRemaining <= 90) {
            expiryContracts.expiringIn90Days.push({ ...contract, daysRemaining });
          }
        });

        setExpiryAnalysis(expiryContracts);
      } catch (error) {
        console.error('Error fetching expiry data:', error);
      }
    };

    if (authState && authState.token) {
      fetchExpiryAnalysis();
    }
  }, [authState]);

  const handleOpenModal = (category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  const getContractsForCategory = () => {
    if (!selectedCategory) return [];
    return expiryAnalysis[selectedCategory] || [];
  };

  const cardData = [
    { key: 'expiringIn90Days', title: 'Expiring in 90 days', icon: IoHourglass, color: 'green' },
    { key: 'expiringIn30Days', title: 'Expiring in 30 days', icon: IoTimeOutline, color: 'blue' },
    { key: 'expiringIn7Days', title: 'Expiring in 7 days', icon: MdEventBusy, color: 'yellow' },
    { key: 'expiredToday', title: 'Expired Today', icon: MdAccessTime, color: 'red' },
  ];

  return (
    <div className="grid w-full sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 ">
      {cardData.map((card) => (
        <div
          key={card.key}
          className="flex flex-col justify-between p-4 bg-white dark:bg-gray-800 shadow-md rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-100"
          onClick={() => handleOpenModal(card.key)}
        >
          <div className="flex items-center justify-between mb-3">
            <card.icon className={`text-3xl text-${card.color}-500 dark:text-${card.color}-400`} />
            <div className={`w-10 h-10 flex items-center justify-center bg-${card.color}-100 dark:bg-${card.color}-800 rounded-full`}>
              <span className={`text-xl font-bold text-${card.color}-600 dark:text-${card.color}-300`}>
                {expiryAnalysis[card.key].length}
              </span>
            </div>
          </div>
          <div className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-2">
            {card.title}
          </div>
          <div className="flex items-center text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors duration-150">
            See More
            <FaChevronRight className="ml-1" />
          </div>
        </div>
      ))}

      <ExpiryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        contracts={getContractsForCategory()}
        category={selectedCategory}
      />
    </div>
  );
};

export default StatCard;
